import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ShieldCheck, Loader2, CheckCircle2 } from "lucide-react";
import { InstituicaoInput } from "@/components/InstituicaoInput";
import { GrauInput } from "@/components/GrauInput";

const empty = () => ({ nome: "", email: "", password: "", grau_nome: "", instituicao_nome: "", unidade: "", pais_id: "", rg: "", matricula: "", identificacao_funcional: "", telefone: "" });

export default function Register() {
  const navigate = useNavigate();
  const [form, setForm] = useState(empty());
  const [listas, setListas] = useState({ grau: [], instituicao: [], pais: [] });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get("/public/listas").then((r) => setListas(r.data)).catch(() => {});
  }, []);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const instNome = (form.instituicao_nome || "").trim();
      const instMatch = listas.instituicao.find((it) => (it.nome || "").toLowerCase() === instNome.toLowerCase());
      const grauNome = (form.grau_nome || "").trim();
      const grauMatch = listas.grau.find((g) => (g.nome || "").toLowerCase() === grauNome.toLowerCase());
      await api.post("/auth/register", {
        nome: form.nome, email: form.email, password: form.password,
        grau_id: grauMatch ? grauMatch.id : null, grau_nome: grauNome || null,
        instituicao_nome: instNome || null, instituicao_id: instMatch ? instMatch.id : null,
        unidade: form.unidade, pais_id: form.pais_id || null,
        rg: form.rg, matricula: form.matricula, identificacao_funcional: form.identificacao_funcional, telefone: form.telefone,
      });
      setDone(true);
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || "Falha no cadastro");
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="w-full max-w-md text-center" data-testid="registro-sucesso">
          <div className="p-3 rounded-xl bg-emerald-500/15 border border-emerald-500/30 mb-4 inline-flex">
            <CheckCircle2 className="h-10 w-10 text-emerald-400" />
          </div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Cadastro enviado</h1>
          <p className="text-sm text-muted-foreground mt-3">
            Sua conta de instrutor foi criada e está <strong>pendente de aprovação</strong> do administrador.
            Você poderá acessar assim que for ativada.
          </p>
          <Button className="mt-6" data-testid="voltar-login-btn" onClick={() => navigate("/login")}>Voltar ao login</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-10">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-6">
          <div className="p-3 rounded-xl bg-primary/15 border border-primary/30 mb-4">
            <ShieldCheck className="h-9 w-9 text-primary" />
          </div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Cadastro de Instrutor</h1>
          <p translate="no" className="notranslate text-xs uppercase tracking-widest text-muted-foreground font-mono mt-1">MÉTRON</p>
        </div>

        <form onSubmit={submit} className="space-y-3 bg-card border border-border rounded-xl p-6">
          <div className="space-y-1.5">
            <Label>Nome completo</Label>
            <Input data-testid="reg-nome" value={form.nome} onChange={(e) => set({ nome: e.target.value })} required />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>E-mail</Label>
              <Input data-testid="reg-email" type="email" value={form.email} onChange={(e) => set({ email: e.target.value })} required />
            </div>
            <div className="space-y-1.5">
              <Label>Senha</Label>
              <Input data-testid="reg-senha" type="password" value={form.password} onChange={(e) => set({ password: e.target.value })} required />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Grau / Posto</Label>
              <GrauInput value={form.grau_nome} onChange={(v) => set({ grau_nome: v })} opcoes={listas.grau} testid="reg-grau" label={null} />
            </div>
            <div className="space-y-1.5">
              <Label>Instituição</Label>
              <InstituicaoInput
                value={form.instituicao_nome}
                onChange={(v) => set({ instituicao_nome: v })}
                opcoes={listas.instituicao}
                testid="reg-instituicao"
                label={null}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Unidade / Organização</Label>
              <Input data-testid="reg-unidade" value={form.unidade} onChange={(e) => set({ unidade: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>País</Label>
              <Select value={form.pais_id} onValueChange={(v) => set({ pais_id: v })}>
                <SelectTrigger data-testid="reg-pais"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  {listas.pais.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>RG</Label><Input data-testid="reg-rg" value={form.rg} onChange={(e) => set({ rg: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Identificação funcional</Label><Input data-testid="reg-idfuncional" value={form.identificacao_funcional} onChange={(e) => set({ identificacao_funcional: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Matrícula</Label>
              <Input data-testid="reg-matricula" value={form.matricula} onChange={(e) => set({ matricula: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Telefone <span className="text-muted-foreground text-xs">(opcional)</span></Label>
              <Input data-testid="reg-telefone" value={form.telefone} onChange={(e) => set({ telefone: e.target.value })} />
            </div>
          </div>
          {error && <p data-testid="reg-error" className="text-sm text-red-400">{error}</p>}
          <Button type="submit" data-testid="reg-submit" disabled={loading} className="w-full">
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Criar conta
          </Button>
          <p className="text-center text-xs text-muted-foreground">
            Já tem conta? <a href="/login" className="text-primary hover:underline">Entrar</a>
          </p>
        </form>
      </div>
    </div>
  );
}
